import { Button, Col, Descriptions, DescriptionsProps, Divider, Flex, Form, Input, Layout, Row, Select, Space, Typography } from "antd"
import { useTranslation } from "react-i18next";
import { User } from "@types";
import { useEffect } from "react";
import { useEditUser, useThemeDetector } from "@hooks";
import dayjs from "dayjs";
import { useExamStore } from "@store";

interface Props {
    user: User;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SettingsDrawer = ({user, setIsModalOpen}: Props) => {

    
    const [settingsForm] = Form.useForm();
    const {t, i18n} = useTranslation();
    const {isDarkTheme} = useThemeDetector();
    const setTheme = useExamStore(state => state.setTheme);
    const theme = useExamStore(state => state.theme);
    const editUser = useEditUser();

    const items: DescriptionsProps["items"] = [
        {
            key: "1",
            label: t("table.dateJoined"),
            children: dayjs(user.dateCreated).format("DD.MM.YYYY")
        },
        {
            key: "2",
            label: t("table.roles"),
            children: user.applicationRoles.map(role => {
              if (role.name === "Admin") {
                return t("roles.admin");
              } else if (role.name === "DataAnalyst") {
                return t("roles.dataAnalyst");
              } else if (role.name === "QuestionAdmin") {
                return t("roles.questionAdmin");
              } else if (role.name === "TermAdmin") {
                return t("roles.termAdmin");
              } else if (role.name === "ExamAdmin") {
                return t("roles.examAdmin");
              } else if (role.name === "ExamParticipant") {
                return t("roles.examParticipant");
              } else if (role.name === "Assessor") {
                return t("roles.assessor");
              } else {
                return role.name;
              }
            }).join(", ")
        }
    ]

    useEffect(() => {
        if (user) {
            settingsForm.setFieldsValue(user);
        }
    }, [user]);

    const onSubmit = (values: any) => {
        console.log(values);
        editUser.mutate({
          id: user.id, 
          ...values }, {
          onSuccess: () => {
            setIsModalOpen(false);
          }
        });
      }

    return (
      <Layout>
        <Form layout="vertical" form={settingsForm} onFinish={onSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={["firstName"]}
                label={t("table.name")}
                rules={[
                  { required: true, message: t("forms.user.firstNameDesc") },
                ]}
              >
                <Input placeholder={t("table.name")} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={["lastName"]}
                label={t("table.lastName")}
                rules={[
                  { required: true, message: t("forms.user.lastNameDesc") },
                ]}
              >
                <Input placeholder={t("table.lastName")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name={["oib"]} label={t("table.oib")}>
                <Input placeholder={t("forms.user.oib")} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={["email"]}
                label={t("table.email")}
                rules={[{ required: true, message: t("forms.user.emailDesc") }]}
              >
                <Input placeholder={t("table.email")} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              marginBottom: 0,
              marginTop: 8,
            }}
          >
            <Button
              type="primary"
              loading={editUser.isPending}
              htmlType="submit"
            >
              {t("forms.questions.save")}
            </Button>
          </Form.Item>
        </Form>
        <Divider />
        <Descriptions items={items} column={1} />
        <Flex gap={'small'} wrap>
        <Space style={{marginBottom: 12}} direction="vertical">
          <Typography.Text type="secondary">{t("language")}: </Typography.Text>
          <Select
            defaultValue={i18n.language}
            style={{ width: 150 }}
            onChange={(value) => {
              i18n.changeLanguage(value)
            }}
          >
            <Select.Option value="en">{t("english")}</Select.Option>
            <Select.Option value="hr">{t("croatian")}</Select.Option>
          </Select>
        </Space>
        <Space direction="vertical">
          <Typography.Text type="secondary">{t("theme")}: </Typography.Text>
          <Select
            // disabled
            defaultValue={theme}
            style={{ width: 150 }}
            onChange={(value) => {
              setTheme(value, isDarkTheme)
            }}
          >
            <Select.Option value={"light"}>{t("light")}</Select.Option>
            <Select.Option value={"dark"}>{t("dark")}</Select.Option>
            <Select.Option value={"system"}>{t("system")}</Select.Option>
          </Select>
        </Space>
        </Flex>
      </Layout>
    );
}